import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { initializeApp, getApps } from 'firebase/app'
import { getDatabase, ref, onValue, set, get, off, update, push } from 'firebase/database'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import {
    gamesAPI, notifyAPI, walletAPI,
    getToken, getMongoId,
} from '../api/api'

// ─── Firebase ──────────────────────────────────────────────────────────────────
const FIREBASE_CONFIG = {
    apiKey:            "AIzaSyD10olyURDLvM2BdtkLTnwVVB98qfNiDcc",
    authDomain:        "clubswap-5a74a.firebaseapp.com",
    databaseURL:       "https://clubswap-5a74a-default-rtdb.firebaseio.com",
    projectId:         "clubswap-5a74a",
    storageBucket:     "clubswap-5a74a.firebasestorage.app",
    messagingSenderId: "274838532963",
    appId:             "1:274838532963:web:4044ba76f6192dcfdf5956",
}

let _app, _db, _auth
function getDB() {
    if (!_db) {
        _app  = getApps().length ? getApps()[0] : initializeApp(FIREBASE_CONFIG)
        _db   = getDatabase(_app)
        _auth = getAuth(_app)
    }
    return _db
}
function getFirebaseAuth() { getDB(); return _auth }

// ─── Constants ─────────────────────────────────────────────────────────────────
const EXPIRY_MS    = 3 * 60 * 1000
const DISPUTE_FEE  = 10
const PLATFORM_FEE = 0.15   // kept in sync with backend

// ─── Status stages ────────────────────────────────────────────────────────────
const STAGES = ['pending', 'accepted', 'playing', 'submitted', 'settled']
const STAGE_LABELS = {
    pending:   'Created',
    accepted:  'Accepted',
    playing:   'Playing',
    submitted: 'Winner Submitted',
    settled:   'Winner Finalised',
    disputed:  'Under Review',
}

function stageIndex(status) {
    return STAGES.indexOf(status)
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtTime(ms) {
    if (ms <= 0) return '0:00'
    const s = Math.floor(ms / 1000)
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}
function prize(stake) { return Math.floor(stake * 2 * (1 - PLATFORM_FEE)) }
function initials(name) {
    if (!name) return '?'
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
}

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function H2HLobby() {
    const { cid }  = useParams()
    const navigate = useNavigate()

    const [currentUser,  setCurrentUser]  = useState(null)
    const [authReady,    setAuthReady]    = useState(false)
    const [myMongoId,    setMyMongoId]    = useState(null)

    const [challenge,    setChallenge]    = useState(null)
    const [loading,      setLoading]      = useState(true)
    const [error,        setError]        = useState('')

    // UI
    const [timeLeft, setTimeLeft]       = useState(null)
    const [roomCode, setRoomCode]       = useState('')
    const [roomCodeInput, setRoomCodeInput] = useState('')
    const [copied, setCopied]           = useState(false)
    const [actionLoading, setActionLoading] = useState(false)
    const [myResult, setMyResult]           = useState(null)
    const [showDisputeInfo, setShowDisputeInfo] = useState(false)
    const [resultSubmitted, setResultSubmitted] = useState(false)

    // Match timer (5 min playing window)
    const [matchTimeLeft, setMatchTimeLeft]     = useState(null)
    const [matchTimerDone, setMatchTimerDone]   = useState(false)

    // Dispute window (3 min after first result submitted)
    const [disputeTimeLeft, setDisputeTimeLeft] = useState(null)
    const [disputeWindowOpen, setDisputeWindowOpen] = useState(false)
    const [showDisputeModal, setShowDisputeModal]   = useState(false)
    const [disputeDepositing, setDisputeDepositing] = useState(false)

    const timerRef        = useRef(null)
    const matchTimerRef   = useRef(null)
    const disputeTimerRef = useRef(null)
    const expiredRef      = useRef(false)
    const settlingRef     = useRef(false)

    // ── Auth ────────────────────────────────────────────────────────────────────
    useEffect(() => {
        const auth = getFirebaseAuth()
        const unsub = onAuthStateChanged(auth, user => {
            setCurrentUser(user)
            setMyMongoId(getMongoId())
            setAuthReady(true)
        })
        return () => unsub()
    }, [])

    // ── Listen to Firebase challenge (real-time coordination) ───────────────────
    useEffect(() => {
        if (!cid) return
        const db    = getDB()
        const chalRef = ref(db, `challenges/${cid}`)
        const unsub = onValue(chalRef, snap => {
            if (!snap.exists()) { setError('Challenge not found.'); setLoading(false); return }
            const data = snap.val()
            setChallenge(data)
            setLoading(false)
            if (currentUser) {
                const isChallenger = data.challengerUid === currentUser.uid
                if (isChallenger  && data.challengerResult) setResultSubmitted(true)
                if (!isChallenger && data.opponentResult)   setResultSubmitted(true)
            }
        })
        return () => off(chalRef)
    }, [cid, currentUser])

    // ── Expiry countdown (pending challenges only) ──────────────────────────────
    useEffect(() => {
        if (!challenge || challenge.status !== 'pending') {
            clearInterval(timerRef.current)
            return
        }
        const tick = () => {
            const left = challenge.expiresAt - Date.now()
            setTimeLeft(left)
            if (left <= 0 && !expiredRef.current) {
                expiredRef.current = true
                handleExpiry()
            }
        }
        tick()
        timerRef.current = setInterval(tick, 1000)
        return () => clearInterval(timerRef.current)
    }, [challenge?.expiresAt, challenge?.status])    // eslint-disable-line

    // ── Auto-settle when both results match ─────────────────────────────────────
    useEffect(() => {
        if (!challenge || challenge.status !== 'submitted' || challenge.settled) return
        if (!challenge.challengerResult || !challenge.opponentResult) return
        if (challenge.challengerResult === challenge.opponentResult) {
            handleSettle(challenge.challengerResult)
        } else {
            handleDispute()
        }
    }, [challenge?.challengerResult, challenge?.opponentResult, challenge?.status])  // eslint-disable-line

    // ── 2-min auto-settle if opponent hasn't responded ──────────────────────────
    useEffect(() => {
        if (!challenge || challenge.status !== 'submitted') return
        if (challenge.challengerResult && challenge.opponentResult) return
        const submittedAt = challenge.resultSubmittedAt
        if (!submittedAt) return

        const deadline = submittedAt + 3 * 60 * 1000
        const left = deadline - Date.now()
        if (left <= 0) {
            const winner = challenge.challengerResult || challenge.opponentResult
            if (winner) handleSettle(winner)
            return
        }
        const t = setTimeout(() => {
            const winner = challenge.challengerResult || challenge.opponentResult
            if (winner) handleSettle(winner)
        }, left)
        return () => clearTimeout(t)
    }, [challenge?.status, challenge?.challengerResult, challenge?.opponentResult, challenge?.resultSubmittedAt])  // eslint-disable-line

    // ── 5-min match timer (playing status) ──────────────────
    useEffect(() => {
        if (!challenge || challenge.status !== 'playing') {
            clearInterval(matchTimerRef.current)
            return
        }
        const playingAt = challenge.playingAt
        if (!playingAt) return  // wait until playingAt is set in Firebase
        const deadline  = playingAt + 5 * 60 * 1000

        const tick = () => {
            const left = deadline - Date.now()
            setMatchTimeLeft(Math.max(0, left))
            if (left <= 0) {
                clearInterval(matchTimerRef.current)
                setMatchTimerDone(true)
            }
        }
        tick()
        matchTimerRef.current = setInterval(tick, 1000)
        return () => clearInterval(matchTimerRef.current)
    }, [challenge?.status, challenge?.playingAt])

    // ── 3-min dispute window after first result submitted ────
    useEffect(() => {
        if (!challenge?.resultSubmittedAt) return
        if (!['playing', 'submitted'].includes(challenge?.status)) return

        const deadline = challenge.resultSubmittedAt + 3 * 60 * 1000
        const left = deadline - Date.now()

        if (left <= 0) {
            setDisputeWindowOpen(false)
            clearInterval(disputeTimerRef.current)
            return
        }

        setDisputeWindowOpen(true)
        const tick = () => {
            const l = deadline - Date.now()
            setDisputeTimeLeft(Math.max(0, l))
            if (l <= 0) {
                clearInterval(disputeTimerRef.current)
                setDisputeWindowOpen(false)
            }
        }
        tick()
        disputeTimerRef.current = setInterval(tick, 1000)
        return () => clearInterval(disputeTimerRef.current)
    }, [challenge?.resultSubmittedAt, challenge?.status])

    // ─────────────────────────────────────────────────────────
    //  DERIVED STATE
    // ─────────────────────────────────────────────────────────
    const isChallenger = currentUser && challenge?.challengerUid === currentUser.uid
    const isOpponent   = currentUser && challenge?.opponentUid   === currentUser.uid
    const isParticipant = isChallenger || isOpponent
    const myUid         = currentUser?.uid
    const oppUid        = isChallenger ? challenge?.opponentUid    : challenge?.challengerUid
    const myMongoIdDerived  = isChallenger ? challenge?.challengerMongoId : challenge?.opponentMongoId
    const oppMongoId    = isChallenger ? challenge?.opponentMongoId : challenge?.challengerMongoId
    const myName        = isChallenger ? challenge?.challengerName  : challenge?.opponentName
    const oppName       = isChallenger ? challenge?.opponentName   : challenge?.challengerName

    // ─── ACTIONS ───────────────────────────────────────────────────────────────

    // Challenge expired before opponent accepted — MongoDB already escrowed challenger's stake;
    // call decline to refund it, then mark Firebase as expired.
    const handleExpiry = useCallback(async () => {
        if (!challenge || challenge.status !== 'pending') return
        const db = getDB()
        try {
            if (challenge.mongoMatchId && getToken()) {
                await gamesAPI.declineChallenge(challenge.mongoMatchId)
            }
        } catch {}
        await update(ref(db, `challenges/${cid}`), { status: 'expired' })
    }, [challenge, cid])

    // Opponent accepts: MongoDB escrows opponent's stake; Firebase status → accepted
    const handleAccept = useCallback(async () => {
        if (!challenge || !currentUser) return
        setActionLoading(true)
        try {
            // MongoDB: escrow opponent's stake
            await gamesAPI.acceptChallenge(challenge.mongoMatchId)

            // Firebase: update coordination status
            const db = getDB()
            await update(ref(db, `challenges/${cid}`), {
                status:        'accepted',
                opponentEscrow: true,
                acceptedAt:    Date.now(),
            })

            // Notify challenger via backend
            await notifyAPI.send(
                challenge.challengerMongoId,
                `✅ <b>Challenge Accepted!</b>\n\n` +
                `<b>${challenge.opponentName}</b> accepted your challenge!\n` +
                `🎮 Game: ${challenge.gameType === 'pool' ? '8 Ball Pool 🎱' : 'Checkers ♟️'}\n` +
                `💎 Total Escrow: <b>${challenge.stake * 2} diamonds</b>\n\n` +
                `🎥 <b>Remember to record your gameplay!</b>`,
            )
        } catch (e) {
            setError(e.message || 'Failed to accept. Try again.')
        }
        setActionLoading(false)
    }, [challenge, currentUser, cid])

    // Decline or Cancel — MongoDB refunds challenger; Firebase marks declined/cancelled
    const handleDeclineOrCancel = useCallback(async () => {
        if (!challenge) return
        setActionLoading(true)
        try {
            // MongoDB: refund challenger's stake (and opponent's if already escrowed)
            if (challenge.mongoMatchId && getToken()) {
                await gamesAPI.declineChallenge(challenge.mongoMatchId)
            }

            const db = getDB()
            await update(ref(db, `challenges/${cid}`), {
                status: isOpponent ? 'declined' : 'cancelled',
            })

            // Notify the other player
            const notifyId  = isOpponent ? challenge.challengerMongoId : challenge.opponentMongoId
            const notifyMsg = isOpponent
                ? `❌ <b>Challenge Declined</b>\n${challenge.opponentName} declined your challenge. Full refund issued.`
                : `❌ <b>Challenge Cancelled</b>\n${challenge.challengerName} cancelled the challenge. Full refund issued.`
            await notifyAPI.send(notifyId, notifyMsg)

            navigate('/')
        } catch (e) {
            setError('Failed. Try again.')
        }
        setActionLoading(false)
    }, [challenge, cid, isOpponent, navigate])

    // Opponent shares game profile / room code (Firebase only — no money)
    const handleSubmitRoomCode = useCallback(async () => {
        if (!roomCodeInput.trim()) return
        setActionLoading(true)
        try {
            const db = getDB()
            await update(ref(db, `challenges/${cid}`), {
                status:    'playing',
                roomCode:  roomCodeInput.trim(),
                playingAt: Date.now(),
            })
            await notifyAPI.send(
                challenge.challengerMongoId,
                `🎮 <b>Game On! Room Code Ready</b>\n\n` +
                `<b>${challenge.opponentName}</b> has shared the room code:\n\n` +
                `🔑 <code>${roomCodeInput.trim()}</code>\n\n` +
                `Game: ${challenge.gameType === 'pool' ? '8 Ball Pool 🎱' : 'Checkers ♟️'}\n` +
                `💎 Prize: <b>${prize(challenge.stake)} diamonds</b>\n\n` +
                `🎥 <b>Don't forget to record your gameplay!</b>`,
            )
        } catch (e) {
            setError('Failed to share code. Try again.')
        }
        setActionLoading(false)
    }, [roomCodeInput, cid, challenge])

    const handleCopy = useCallback(() => {
        if (!challenge?.roomCode) return
        navigator.clipboard.writeText(challenge.roomCode)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }, [challenge?.roomCode])

    // Submit result — record in Firebase; auto-settle runs when both sides submit
    const handleSubmitResult = useCallback(async (whoWon) => {
        if (!challenge || !currentUser) return
        setActionLoading(true)
        setMyResult(whoWon)
        try {
            const db = getDB()
            // winnerUid here is a Firebase UID (used for the auto-settle comparison)
            const winnerUid  = whoWon === 'me' ? myUid : oppUid
            const updateData = {
                status:            'submitted',
                resultSubmittedAt: challenge.resultSubmittedAt || Date.now(),
            }
            if (isChallenger) updateData.challengerResult = winnerUid
            else              updateData.opponentResult   = winnerUid

            await update(ref(db, `challenges/${cid}`), updateData)
            setResultSubmitted(true)

            // Notify opponent to also submit
            await notifyAPI.send(oppMongoId,
                `⚔️ *Result Submitted*\n\n` +
                `*${myName}* has submitted the match result.\n` +
                `Please open the app and confirm the winner within *3 minutes* or it will auto-settle.\n\n` +
                `🎥 If there's a dispute, you may be asked to share your gameplay recording.`
            )
        } catch (e) {
            setError('Failed to submit result. Try again.')
        }
        setActionLoading(false)
    }, [challenge, currentUser, myUid, oppUid, oppMongoId, isChallenger, cid, myName])

    // Settle — MongoDB commits first (source of truth), then Firebase is updated for UI
    const handleSettle = useCallback(async (winnerFirebaseUid) => {
        if (!challenge || challenge.settled || settlingRef.current) return
        settlingRef.current = true

        try {
            const db = getDB()
            // Guard against double-settle using Firebase flag
            const freshSnap = await get(ref(db, `challenges/${cid}/settled`))
            if (freshSnap.val() === true) return

            // Resolve MongoDB winner ID from the Firebase UID
            const winnerMongoId = winnerFirebaseUid === challenge.challengerUid
                ? challenge.challengerMongoId
                : challenge.opponentMongoId

            // MongoDB FIRST: atomic escrow release + payout (source of truth)
            const result = await gamesAPI.settleMatch(challenge.mongoMatchId, winnerMongoId)

            const winnerName   = winnerFirebaseUid === challenge.challengerUid
                ? challenge.challengerName : challenge.opponentName
            const loserName    = winnerFirebaseUid === challenge.challengerUid
                ? challenge.opponentName   : challenge.challengerName
            const loserMongoId = winnerFirebaseUid === challenge.challengerUid
                ? challenge.opponentMongoId : challenge.challengerMongoId
            const payout = result.winnerPayout || prize(challenge.stake)

            // Firebase AFTER MongoDB success: mark settled + update UI state
            await update(ref(db, `challenges/${cid}`), {
                settled:   true,
                status:    'settled',
                winner:    winnerFirebaseUid,
                payout,
                settledAt: Date.now(),
            })

            // Notify both players
            await notifyAPI.send(
                winnerMongoId,
                `🏆 <b>You Won!</b>\n\nCongratulations! You beat <b>${loserName}</b>.\n` +
                `💎 <b>+${payout} diamonds</b> added to your balance!` +
                `${result.leveledUp ? '\n\n⬆️ Level up! 🎉' : ''}`,
            )
            await notifyAPI.send(
                loserMongoId,
                `💔 <b>Better luck next time</b>\n\n<b>${winnerName}</b> won this match.\nKeep practising and challenge again! 💪`,
            )
        } catch (e) {
            console.error('Settle error:', e)
            settlingRef.current = false  // allow retry if MongoDB settlement failed
        }
    }, [challenge, cid])

    // ── Create dispute record & navigate ────────────────────
    const createDisputeAndNavigate = useCallback(async (isManual = false) => {
        if (!challenge) return
        try {
            const db = getDB()
            const evidenceDeadline = Date.now() + 3 * 60 * 1000  // 3 min evidence window

            const disputeRef = push(ref(db, 'disputes'))
            const did = disputeRef.key

            await set(disputeRef, {
                did,
                type:           'h2h',
                challengeId:    cid,
                status:         'evidence',
                autoDispute:    !isManual,
                depositPaid:    isManual,
                gameType:       challenge.gameType,
                stake:          challenge.stake,
                parties:        [challenge.challengerUid, challenge.opponentUid],
                partyNames: {
                    [challenge.challengerUid]: challenge.challengerName,
                    [challenge.opponentUid]:   challenge.opponentName,
                },
                claims: {
                    [challenge.challengerUid]: challenge.challengerResult
                        ? (challenge.challengerResult === challenge.challengerUid ? 'I Won' : 'Opponent Won')
                        : 'Not submitted',
                    [challenge.opponentUid]: challenge.opponentResult
                        ? (challenge.opponentResult === challenge.opponentUid ? 'I Won' : 'Opponent Won')
                        : 'Not submitted',
                },
                evidence:         {},
                evidenceDeadline,
                createdAt:        Date.now(),
                winner:           null,
                penalisedUid:     null,
                adminNote:        '',
                resolvedAt:       null,
            })

            // Link dispute to challenge
            await update(ref(db, `challenges/${cid}`), {
                status:    'disputed',
                disputeId: did,
                disputedAt: Date.now(),
            })

            // Notify both
            const msg = `⚔️ *Match Disputed*\n\n` +
                `The result for *${challenge.challengerName}* vs *${challenge.opponentName}* is under review.\n\n` +
                `📹 You have *3 minutes* to submit your evidence link.\n` +
                `⚠️ No evidence = decision may not favour you.\n\n` +
                `Open the app now to submit.`

            await notifyAPI.send(challenge.challengerMongoId, msg)
            await notifyAPI.send(challenge.opponentMongoId, msg)

            navigate(`/dispute/${did}`)
        } catch (e) {
            console.error('Dispute error:', e)
        }
    }, [challenge, cid, navigate])

    // ── Auto dispute (mismatch) ───────────────────────────────
    const handleDispute = useCallback(async () => {
        await createDisputeAndNavigate(false)
    }, [createDisputeAndNavigate])

    // ── Manual dispute (with 10💎 deposit) ───────────────────
    const handleManualDispute = useCallback(async () => {
        if (!challenge || !currentUser) return
        setDisputeDepositing(true)
        try {
            const db = getDB()
            const balSnap = await get(ref(db, `users/${currentUser.uid}/balance`))
            const bal = balSnap.val()?.diamonds ?? 0
            if (bal < DISPUTE_FEE) {
                alert(`Not enough 💎. You need ${DISPUTE_FEE} diamonds to raise a dispute.`)
                setDisputeDepositing(false)
                return
            }
            // Deduct deposit
            await set(ref(db, `users/${currentUser.uid}/balance`), { diamonds: bal - DISPUTE_FEE })
            await push(ref(db, `users/${currentUser.uid}/history`), {
                type: 'entry',
                amount: -DISPUTE_FEE,
                description: 'Dispute deposit — refunded if your claim is correct',
                timestamp: Date.now(),
            })
            await createDisputeAndNavigate(true)
        } catch (e) {
            console.error('Manual dispute error:', e)
        }
        setDisputeDepositing(false)
    }, [challenge, currentUser, createDisputeAndNavigate])

    // ─── RENDER HELPERS ────────────────────────────────────────────────────────

    const StatusBar = () => {
        const current = stageIndex(challenge?.status)
        const statusToShow = ['disputed', 'settled'].includes(challenge?.status)
            ? challenge?.status : challenge?.status
        const stages  = ['pending', 'accepted', 'playing', 'submitted', 'settled']
        return (
            <div style={s.statusBar}>
                {stages.map((stage, i) => {
                    const done   = i < current
                    const active = i === current
                    return (
                        <React.Fragment key={stage}>
                            <div style={s.stageWrap}>
                                <div style={done ? s.stageDotDone : active ? s.stageDotActive : s.stageDotLocked}>
                                    {done ? '✓' : !active ? '🔒' : ''}
                                </div>
                                <div style={done || active ? s.stageLabel : s.stageLabelDim}>{STAGE_LABELS[stage]}</div>
                            </div>
                            {i < stages.length - 1 && (
                                <div style={i < current ? s.connectorDone : s.connectorPending} />
                            )}
                        </React.Fragment>
                    )
                })}
            </div>
        )
    }

    const EscrowBadge = () => (
        <div style={s.escrowBadge}>
            <div style={s.escrowLabel}>In Escrow</div>
            <div style={s.escrowAmount}>{challenge.stake * 2} 💎</div>
            <div style={s.escrowSub}>Diamonds</div>
            <div style={s.escrowNote}>
                {challenge.stake} 💎 from each player · Winner gets {prize(challenge.stake)} 💎
            </div>
        </div>
    )

    // ─── RENDER ────────────────────────────────────────────────────────────────

    if (!authReady || loading) return (
        <div style={s.root}><div style={s.centered}><div style={s.spinner} /><div style={s.loadingText}>Loading challenge…</div></div></div>
    )

    if (error && !challenge) return (
        <div style={s.root}><div style={s.centered}><div style={s.errorBig}>{error}</div><button style={s.backBtn} onClick={() => navigate('/')}>← Back to Home</button></div></div>
    )

    if (!isParticipant && authReady) return (
        <div style={s.root}><div style={s.centered}><div style={s.errorBig}>⛔ You're not part of this challenge.</div><button style={s.backBtn} onClick={() => navigate('/')}>← Back to Home</button></div></div>
    )

    if (['expired', 'cancelled', 'declined'].includes(challenge?.status)) return (
        <div style={s.root}><div style={s.page}>
            <div style={s.header}><button style={s.backIcon} onClick={() => navigate('/')}>←</button><span style={s.headerTitle}>H2H Challenge</span></div>
            <div style={s.centered}>
                <div style={{ fontSize: 56, marginBottom: 16 }}>❌</div>
                <div style={s.bigLabel}>{challenge.status === 'expired' ? 'Challenge Expired' : challenge.status === 'declined' ? 'Challenge Declined' : 'Challenge Cancelled'}</div>
                <div style={s.subLabel}>Your entry fee has been fully refunded.</div>
                <button style={s.greenBtn} onClick={() => navigate('/')}>Back to Home</button>
            </div>
        </div></div>
    )

    if (challenge?.status === 'settled') {
        const iWon = challenge.winner === myUid
        const winnerName = challenge.winner === challenge.challengerUid
            ? challenge.challengerName : challenge.opponentName
        return (
            <div style={s.root}>
                <div style={s.page}>
                    <div style={s.header}>
                        <button style={s.backIcon} onClick={() => navigate('/')}>←</button>
                        <span style={s.headerTitle}>Match Finalised</span>
                    </div>
                    {/* Completed progress bar */}
                    <div style={s.card}>
                        <div style={s.statusBar}>
                            {['pending','accepted','playing','submitted','settled'].map((stage, i) => (
                                <React.Fragment key={stage}>
                                    <div style={s.stageWrap}>
                                        <div style={s.stageDotDone}>✓</div>
                                        <div style={s.stageLabel}>{STAGE_LABELS[stage]}</div>
                                    </div>
                                    {i < 4 && <div style={s.connectorDone} />}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                    <div style={s.centered}>
                        <div style={{ fontSize: 64, marginBottom: 12 }}>{iWon ? '🏆' : '💔'}</div>
                        <div style={{ ...s.bigLabel, color: iWon ? '#4ade80' : '#f87171' }}>
                            {iWon ? '🎉 Winner Finalised!' : 'Match Closed'}
                        </div>
                        {iWon ? (
                            <>
                                <div style={s.payoutBadge}>+{challenge.payout} 💎 added to your balance</div>
                                <div style={s.subLabel}>Great game against {oppName}! Keep it up!</div>
                            </>
                        ) : (
                            <>
                                <div style={{ ...s.subLabel, color: '#9ca3af' }}>
                                    🏆 {winnerName} was declared the winner.
                                </div>
                                <div style={s.subLabel}>Better luck next time!</div>
                            </>
                        )}
                        <div style={{ background: '#0d1520', border: '1px solid #1a2a3a', borderRadius: 8, padding: '10px 14px', marginTop: 12, textAlign: 'center' }}>
                            <div style={{ fontSize: 12, color: '#4a7aaa' }}>🔒 Lobby Closed</div>
                        </div>
                        <button style={{ ...s.greenBtn, marginTop: 16 }} onClick={() => navigate('/')}>Back to Home</button>
                    </div>
                </div>
            </div>
        )
    }

    // ── DISPUTED ──────────────────────────────────────────────
    if (challenge?.status === 'disputed') {
        return (
            <div style={s.root}>
                <div style={s.page}>
                    <div style={s.header}>
                        <button style={s.backIcon} onClick={() => navigate('/')}>←</button>
                        <span style={s.headerTitle}>⚔️ Under Admin Review</span>
                    </div>
                    <div style={s.card}>
                        <div style={{ fontSize: 48, textAlign: 'center', marginBottom: 12 }}>⚠️</div>
                        <div style={s.bigLabel}>Match Under Review</div>
                        <div style={{ background: '#1a0d0d', border: '1px solid #3a1a1a', borderRadius: 8, padding: '8px 14px', marginBottom: 12, textAlign: 'center' }}>
                            <div style={{ fontSize: 12, color: '#fbbf24' }}>🔒 Lobby Closed — Awaiting Admin Decision</div>
                        </div>
                        <div style={s.disputeBox}>
                            <p style={s.disputeText}>
                                This match has a disputed result and is being reviewed by our team.
                            </p>
                            <p style={s.disputeText}>
                                🕒 Resolution may take up to <strong>24 hours</strong>.
                            </p>
                            <p style={s.disputeText}>
                                📹 You may be asked to submit your <strong>gameplay recording</strong> as evidence.
                            </p>
                            <p style={{ ...s.disputeText, color: '#f87171' }}>
                                ⚠️ Players found to have submitted false results may be penalised <strong>{DISPUTE_FEE} 💎</strong>.
                            </p>
                        </div>
                        <button style={s.outlineBtn} onClick={() => navigate('/')}>Back to Home</button>
                    </div>
                </div>
                
            </div>
    )
    }

    // ── MAIN LOBBY ──────────────────────────────────────────────────────────────
    return (
        <div style={s.root}>
            <div style={s.page}>
                <div style={s.header}>
                    <button style={s.backIcon} onClick={() => navigate('/')}>←</button>
                    <span style={s.headerTitle}>My H2H Challenge</span>
                </div>

                {error && <div style={s.errorMsg}>{error}</div>}

                <EscrowBadge />

                <div style={s.card}>
                    <div style={s.playersRow}>
                        <div style={s.playerChip}>
                            <div style={s.playerAvatar}>{initials(challenge.challengerName)}</div>
                            <div style={s.playerChipName}>{challenge.challengerName}</div>
                            <div style={s.playerChipRole}>Challenger</div>
                        </div>
                        <div style={s.vsText}>VS</div>
                        <div style={s.playerChip}>
                            <div style={s.playerAvatar}>{initials(challenge.opponentName)}</div>
                            <div style={s.playerChipName}>{challenge.opponentName}</div>
                            <div style={s.playerChipRole}>Opponent</div>
                        </div>
                    </div>
                    <div style={s.gameTag}>{challenge.gameType === 'pool' ? '🎱 8 Ball Pool' : '♟️ Checkers'}</div>
                </div>

                <div style={s.card}><StatusBar /></div>

                {/* PENDING */}
                {challenge.status === 'pending' && (
                    <div style={s.card}>
                        {isChallenger ? (
                            <>
                                <div style={s.waitingTitle}>⏳ Waiting for {challenge.opponentName} to accept…</div>
                                {timeLeft !== null && (
                                    <div style={s.expiryRow}>
                                        <span style={s.expiryLabel}>Expires in</span>
                                        <span style={{ ...s.expiryTimer, color: timeLeft < 60000 ? '#f87171' : '#4ade80' }}>{fmtTime(timeLeft)}</span>
                                    </div>
                                )}
                                <div style={s.recordReminder}>🎥 <strong>Get ready to record!</strong> Once accepted, start recording your gameplay.</div>
                                <button style={s.dangerOutlineBtn} onClick={handleDeclineOrCancel} disabled={actionLoading}>
                                    {actionLoading ? 'Cancelling…' : 'Cancel Challenge — Full Refund'}
                                </button>
                            </>
                        ) : (
                            <>
                                <div style={s.waitingTitle}>⚔️ {challenge.challengerName} challenged you!</div>
                                <div style={s.stakeInfo}>Your entry: <strong>💎 {challenge.stake}</strong> &nbsp;|&nbsp; Prize: <strong>💎 {prize(challenge.stake)}</strong></div>
                                {timeLeft !== null && (
                                    <div style={s.expiryRow}>
                                        <span style={s.expiryLabel}>Expires in</span>
                                        <span style={{ ...s.expiryTimer, color: timeLeft < 60000 ? '#f87171' : '#4ade80' }}>{fmtTime(timeLeft)}</span>
                                    </div>
                                )}
                                <div style={s.recordReminder}>🎥 <strong>Record your gameplay!</strong> If you accept, please start a screen recording before launching the game.</div>
                                <div style={s.actionRow}>
                                    <button style={s.dangerOutlineBtn} onClick={handleDeclineOrCancel} disabled={actionLoading}>Decline</button>
                                    <button style={s.greenBtn} onClick={handleAccept} disabled={actionLoading}>
                                        {actionLoading ? 'Accepting…' : `✅ Accept — Pay 💎 ${challenge.stake}`}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* ACCEPTED */}
                {challenge.status === 'accepted' && (
                    <div style={s.card}>
                        {isOpponent ? (
                            <>
                                <div style={s.waitingTitle}>🔑 Share Your Game Profile ID</div>
                                <p style={s.instructionText}>Enter your <strong>{challenge.gameType === 'pool' ? '8 Ball Pool' : 'Checkers'}</strong> in-game profile ID or username below.</p>
                                <div style={s.recordReminder}>🎥 <strong>Start recording now!</strong> Begin your screen recording before launching the game.</div>
                                <input style={s.input} placeholder="Enter your game profile ID / username…" value={roomCodeInput} onChange={e => setRoomCodeInput(e.target.value)} />
                                <button style={s.greenBtn} onClick={handleSubmitRoomCode} disabled={actionLoading || !roomCodeInput.trim()}>
                                    {actionLoading ? 'Sharing…' : '📤 Share Code with Opponent'}
                                </button>
                            </>
                        ) : (
                            <>
                                <div style={s.waitingTitle}>⏳ Waiting for {challenge.opponentName} to share the room code…</div>
                                <div style={s.recordReminder}>🎥 <strong>Get ready to record!</strong> Start your screen recording before you join the game room.</div>
                                <p style={s.instructionText}>You'll be notified as soon as {challenge.opponentName} shares their game profile ID.</p>
                            </>
                        )}
                    </div>
                )}

                {/* PLAYING / SUBMITTED */}
                {(challenge.status === 'playing' || challenge.status === 'submitted') && (
                    <>
                        <div style={s.card}>
                            <div style={s.roomCodeLabel}>{isOpponent ? 'Your Game Profile ID (shared)' : 'Room Code / Profile ID'}</div>
                            <div style={s.roomCodeBox}>
                                <span style={s.roomCodeText}>{challenge.roomCode}</span>
                                <button style={s.copyBtn} onClick={handleCopy}>{copied ? '✅ Copied' : '📋 Copy'}</button>
                            </div>
                            {isChallenger && (
                                <p style={s.instructionText}>Use this code to find {challenge.opponentName} in <strong>{challenge.gameType === 'pool' ? '8 Ball Pool' : 'Checkers'}</strong> and join their room.</p>
                            )}
                        </div>

                        <div style={s.recordCard}>
                            <span style={{ fontSize: 20 }}>🎥</span>
                            <div>
                                <div style={s.recordTitle}>Recording Reminder</div>
                                <div style={s.recordBody}>
                                    Keep your screen recording running throughout the match.
                                    If a dispute is raised, both players will be asked to submit their recordings.
                                    Unnecessary disputes are penalised <strong>{DISPUTE_FEE} 💎</strong>.
                                </div>
                            </div>
                        </div>

                        {/* Match timer */}
                        {!matchTimerDone && matchTimeLeft !== null && (
                            <div style={s.matchTimerCard}>
                                <div style={s.matchTimerLabel}>⏱ Match in progress</div>
                                <div style={{
                                    ...s.matchTimerValue,
                                    color: matchTimeLeft < 60000 ? '#f87171' : '#4ade80'
                                }}>{fmtTime(matchTimeLeft)}</div>
                                <div style={s.matchTimerSub}>
                                    Result buttons unlock after the timer ends
                                </div>
                            </div>
                        )}

                        {/* Submit result */}
                        <div style={s.card}>
                            <div style={s.waitingTitle}>
                                {challenge.status === 'submitted'
                                    ? resultSubmitted ? '✅ Result submitted — waiting for opponent…' : '⚔️ Opponent submitted a result — confirm yours'
                                    : '🏁 Submit Match Result'}
                            </div>

                            {!matchTimerDone ? (
                                <p style={{ ...s.instructionText, color: '#4a5a4a', textAlign: 'center' }}>
                                    ⏳ Result buttons will unlock once the 5-minute match timer ends.
                                </p>
                            ) : !resultSubmitted ? (
                                <>
                                    <p style={s.instructionText}>Who won the match? Both players must submit independently. Agreed → auto-settled. Disagreed → 24h review.</p>
                                    <div style={s.actionRow}>
                                        <button style={s.resultBtn} onClick={() => handleSubmitResult('me')} disabled={actionLoading}>🏆 I Won</button>
                                        <button style={s.resultBtnAlt} onClick={() => handleSubmitResult('opponent')} disabled={actionLoading}>{oppName} Won</button>
                                    </div>
                                </>
                            ) : (
                                <p style={s.instructionText}>Your result has been recorded. Waiting for {oppName} to confirm. Auto-settles in 2 minutes if no response.</p>
                            )}
                        </div>

                        {/* Dispute button — only after first result submitted and within 3-min window */}
                        {matchTimerDone && disputeWindowOpen && !resultSubmitted && (
                            <div style={s.disputeCard}>
                                <div style={s.disputeTimerRow}>
                                    <span style={s.disputeLabel}>⚠️ Dispute window</span>
                                    <span style={{
                                        ...s.disputeTimer,
                                        color: disputeTimeLeft < 30000 ? '#f87171' : '#fbbf24'
                                    }}>{fmtTime(disputeTimeLeft)}</span>
                                </div>
                                <p style={s.disputeNote}>
                                    If you believe the result is wrong, raise a dispute.
                                    Costs <strong>💎 {DISPUTE_FEE}</strong> deposit — refunded if your claim is correct.
                                </p>
                                <button
                                    style={s.disputeBtn}
                                    onClick={() => setShowDisputeModal(true)}
                                    disabled={disputeDepositing}
                                >
                                    🚩 Raise Dispute — Pay 💎 {DISPUTE_FEE}
                                </button>
                            </div>
                        )}

                        {/* Dispute confirm modal */}
                        {showDisputeModal && (
                            <div style={s.overlayInline}>
                                <div style={s.disputeModalCard}>
                                    <div style={{ fontSize: 36, textAlign: 'center' }}>🚩</div>
                                    <div style={s.disputeModalTitle}>Raise a Dispute?</div>
                                    <p style={s.disputeModalBody}>
                                        You'll pay <strong>💎 {DISPUTE_FEE}</strong> as a deposit.
                                        This is <strong>refunded</strong> if your claim is found to be correct.
                                        If your claim is wrong, you lose the deposit + a further penalty may apply.
                                    </p>
                                    <p style={s.disputeModalBody}>
                                        🎥 You'll need to submit a <strong>YouTube or Google Drive</strong> link
                                        of your recording as evidence.
                                    </p>
                                    <div style={s.actionRow}>
                                        <button
                                            style={s.cancelBtn}
                                            onClick={() => setShowDisputeModal(false)}
                                            disabled={disputeDepositing}
                                        >Cancel</button>
                                        <button
                                            style={s.disputeConfirmBtn}
                                            onClick={handleManualDispute}
                                            disabled={disputeDepositing}
                                        >
                                            {disputeDepositing ? 'Processing…' : `Pay 💎 ${DISPUTE_FEE} & Dispute`}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {['pending', 'accepted'].includes(challenge.status) && isChallenger && challenge.status === 'accepted' && (
                    <button style={{ ...s.dangerOutlineBtn, marginTop: 0 }} onClick={handleDeclineOrCancel} disabled={actionLoading}>
                        Cancel Match — Both Players Refunded
                    </button>
                )}
            </div>
        </div>
    )
}

// ─── Styles ────────────────────────────────────────────────────────────────────
const s = {
    root: { minHeight: '100vh', background: '#071309', color: '#e8f5e9', fontFamily: "'Segoe UI', system-ui, sans-serif" },
    page: { maxWidth: 480, margin: '0 auto', padding: '0 16px 100px', display: 'flex', flexDirection: 'column', gap: 12 },
    centered: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 16, padding: 24, textAlign: 'center' },
    header: { display: 'flex', alignItems: 'center', gap: 12, padding: '16px 0 8px' },
    backIcon: { background: 'none', border: 'none', color: '#4ade80', fontSize: 22, cursor: 'pointer', padding: '4px 8px' },
    headerTitle: { fontSize: 18, fontWeight: 700, color: '#e8f5e9' },
    card: { background: '#0d1f0f', border: '1px solid #1a3a1c', borderRadius: 14, padding: '18px 16px', display: 'flex', flexDirection: 'column', gap: 12 },
    escrowBadge: { background: 'linear-gradient(135deg, #1a0a3a, #2a1a5a)', border: '1px solid #6b46c1', borderRadius: 14, padding: '20px 16px', textAlign: 'center' },
    escrowLabel: { fontSize: 12, fontWeight: 700, letterSpacing: '0.12em', color: '#a78bfa', marginBottom: 6, background: '#2d1b69', display: 'inline-block', padding: '3px 12px', borderRadius: 20 },
    escrowAmount: { fontSize: 42, fontWeight: 900, color: '#fff', lineHeight: 1.1 },
    escrowSub: { fontSize: 18, fontWeight: 700, color: '#c4b5fd', marginBottom: 6 },
    escrowNote: { fontSize: 12, color: '#7c6fad' },
    playersRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    playerChip: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flex: 1 },
    playerAvatar: { width: 48, height: 48, borderRadius: '50%', background: '#1a3a1c', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16, color: '#4ade80', border: '2px solid #2a5a2c' },
    playerChipName: { fontSize: 13, fontWeight: 700, color: '#e8f5e9', textAlign: 'center' },
    playerChipRole: { fontSize: 11, color: '#4a7a50' },
    vsText: { fontSize: 20, fontWeight: 900, color: '#4ade80', padding: '0 8px' },
    gameTag: { textAlign: 'center', fontSize: 13, color: '#4a7a50', background: '#081508', borderRadius: 8, padding: '6px 12px' },
    statusBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0' },
    stageWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flex: 1 },
    stageDotDone: { width: 28, height: 28, borderRadius: '50%', background: '#4ade80', color: '#071309', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800 },
    stageDotActive: { width: 28, height: 28, borderRadius: '50%', background: '#1a3a1c', border: '2px solid #4ade80', color: '#4ade80', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 },
    stageDotLocked: { width: 28, height: 28, borderRadius: '50%', background: '#0a1a0c', border: '1px solid #1a3a1c', color: '#2a4a2c', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 },
    stageLabel: { fontSize: 9, color: '#4ade80', textAlign: 'center', letterSpacing: '0.04em' },
    stageLabelDim: { fontSize: 9, color: '#2a4a2c', textAlign: 'center' },
    connectorDone: { flex: 0.3, height: 2, background: '#4ade80', borderRadius: 1, marginBottom: 16 },
    connectorPending: { flex: 0.3, height: 2, background: '#1a3a1c', borderRadius: 1, marginBottom: 16 },
    waitingTitle: { fontSize: 15, fontWeight: 700, color: '#e8f5e9', textAlign: 'center' },
    subLabel: { fontSize: 14, color: '#4a7a50', textAlign: 'center' },
    bigLabel: { fontSize: 24, fontWeight: 800, color: '#e8f5e9', textAlign: 'center' },
    stakeInfo: { fontSize: 13, color: '#4a7a50', textAlign: 'center' },
    instructionText: { fontSize: 13, color: '#4a7a50', lineHeight: 1.6, margin: 0 },
    expiryRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 },
    expiryLabel: { fontSize: 13, color: '#4a7a50' },
    expiryTimer: { fontSize: 28, fontWeight: 800, fontVariantNumeric: 'tabular-nums' },
    recordReminder: { background: '#0a1a05', border: '1px solid #2a5a1a', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#6aaa40', lineHeight: 1.5 },
    recordCard: { background: '#0a1a05', border: '1px solid #2a5a1a', borderRadius: 14, padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'flex-start' },
    recordTitle: { fontSize: 13, fontWeight: 700, color: '#4ade80', marginBottom: 4 },
    recordBody: { fontSize: 12, color: '#4a7a50', lineHeight: 1.5 },
    roomCodeLabel: { fontSize: 11, color: '#4a7a50', letterSpacing: '0.08em' },
    roomCodeBox: { display: 'flex', alignItems: 'center', gap: 10, background: '#081508', border: '1px solid #2a5a2c', borderRadius: 10, padding: '12px 16px' },
    roomCodeText: { flex: 1, fontSize: 18, fontWeight: 800, color: '#4ade80', letterSpacing: '0.05em' },
    copyBtn: { padding: '6px 14px', borderRadius: 6, background: '#1a3a1c', border: '1px solid #2a5a2c', color: '#4ade80', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0 },
    greenBtn: { padding: '13px 20px', borderRadius: 12, background: '#4ade80', border: 'none', color: '#071309', fontWeight: 800, fontSize: 15, cursor: 'pointer', textAlign: 'center' },
    outlineBtn: { padding: '12px 20px', borderRadius: 12, background: 'transparent', border: '1px solid #1a3a1c', color: '#4a7a50', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
    dangerOutlineBtn: { padding: '11px 16px', borderRadius: 10, background: 'transparent', border: '1px solid #dc2626', color: '#f87171', fontWeight: 600, fontSize: 13, cursor: 'pointer', textAlign: 'center' },
    backBtn: { padding: '11px 24px', borderRadius: 10, background: '#1a3a1c', border: '1px solid #2a5a2c', color: '#4ade80', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
    actionRow: { display: 'flex', gap: 10 },
    resultBtn: { flex: 1, padding: '13px', borderRadius: 10, background: '#4ade80', border: 'none', color: '#071309', fontWeight: 800, fontSize: 14, cursor: 'pointer' },
    resultBtnAlt: { flex: 1, padding: '13px', borderRadius: 10, background: 'transparent', border: '1px solid #1a3a1c', color: '#4a7a50', fontWeight: 700, fontSize: 14, cursor: 'pointer' },
    disputeBox: { background: '#1a0a0a', border: '1px solid #7f1d1d', borderRadius: 10, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 },
    disputeText: { fontSize: 13, color: '#fca5a5', margin: 0, lineHeight: 1.5 },

    // Match timer
    matchTimerCard: { background: '#0a1a0c', border: '1px solid #2a5a2c', borderRadius: 12, padding: '16px', textAlign: 'center' },
    matchTimerLabel: { fontSize: 11, color: '#4ade80', letterSpacing: '0.08em', marginBottom: 4 },
    matchTimerValue: { fontSize: 40, fontWeight: 900, fontVariantNumeric: 'tabular-nums', lineHeight: 1 },
    matchTimerSub: { fontSize: 11, color: '#2a5a2c', marginTop: 4 },

    // Dispute
    disputeCard: { background: '#1a0a00', border: '1px solid #92400e', borderRadius: 12, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 },
    disputeTimerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    disputeLabel: { fontSize: 12, fontWeight: 700, color: '#fbbf24' },
    disputeTimer: { fontSize: 20, fontWeight: 800, fontVariantNumeric: 'tabular-nums' },
    disputeNote: { fontSize: 12, color: '#92400e', margin: 0, lineHeight: 1.5 },
    disputeBtn: { padding: '11px', borderRadius: 10, background: 'transparent', border: '1px solid #dc2626', color: '#f87171', fontWeight: 700, fontSize: 13, cursor: 'pointer', textAlign: 'center' },

    overlayInline: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
    disputeModalCard: { background: '#0d1f0f', border: '1px solid #2a5a2c', borderRadius: 16, padding: '28px 24px', width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 12 },
    disputeModalTitle: { fontSize: 18, fontWeight: 800, color: '#e8f5e9', textAlign: 'center' },
    disputeModalBody: { fontSize: 13, color: '#4a7a50', lineHeight: 1.6, margin: 0 },
    disputeConfirmBtn: { flex: 2, padding: '12px', borderRadius: 10, background: '#dc2626', border: 'none', color: '#fff', fontWeight: 800, fontSize: 14, cursor: 'pointer' },

    // Misc
    errorMsg: {
        background: '#2a0a0a', border: '1px solid #f44336',
        borderRadius: 8, padding: '10px 14px',
        color: '#f44336', fontSize: 13,
    },
    errorBig: { fontSize: 18, color: '#f87171', marginBottom: 8 },
    payoutBadge: { background: '#0a2a0c', border: '1px solid #2a5a2c', borderRadius: 10, padding: '10px 20px', fontSize: 16, fontWeight: 700, color: '#4ade80' },
    loadingText: { color: '#4a7a50', fontSize: 14 },
    spinner: { width: 36, height: 36, borderRadius: '50%', border: '3px solid #1a3a1c', borderTop: '3px solid #4ade80', animation: 'spin 0.8s linear infinite' },
    input: { width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid #1a3a1c', background: '#081508', color: '#e8f5e9', fontSize: 15, boxSizing: 'border-box' },
}
